import { createSlice } from '@reduxjs/toolkit';

import {
  attachUserToShareProfile,
  createBodyArt,
  deleteBodyArt,
  detachUserFromShareProfile,
  getBodyArts,
  getHealthInsurances,
  getProfileNewDocuments,
  getProfileNewInfo,
  getProfileSharingUsers,
  updateBodyArt,
  updateProfileAppearance,
  updateProfileNewGeneralInfo,
  updateUserShareProfilePermissions,
} from './personal.actions';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { AvatarModel } from '@shared/models/avatar.model';
import type { MediaType } from '@shared/models/media.model';
import type {
  HealthInsuranceRecognitionData,
  HealthInsurances,
} from '@shared/models/profile/profileHealthInsurance.model';
import type {
  BodyArtModel,
  BodyArtsMetaCustom,
  PersonalModel,
  ProfileUserSharingModel,
} from '@shared/models/profileNew/profileNew.model';
import type { Nullable } from '@shared/types/base-custom.type';

export interface ProfileNewState {
  isFetching: boolean;
  data: Nullable<PersonalModel>;
  bodyArts: BodyArtModel[] | null;
  isLoadingBodyArts: boolean;
  bodyArtsMeta: BodyArtsMetaCustom;
  healthInsurances: HealthInsurances[] | null;
  documents: MediaType[];
  isLoadingDocument: boolean;
  sharingPermissions: ProfileUserSharingModel[] | null;
  isLoadingSharingPermissions: boolean;
  isLoadingPersonal: boolean;
  isLoadingHealthInsurance: boolean;
  editMode: boolean;

  scanRecognitionData: Nullable<HealthInsuranceRecognitionData>;
  scanDocument: Nullable<MediaType>;
}

const initialState: ProfileNewState = {
  isFetching: false,
  data: null,
  bodyArts: null,
  isLoadingBodyArts: false,
  bodyArtsMeta: {
    page: 1,
    isStopPagination: false,
  },
  healthInsurances: null,
  isLoadingHealthInsurance: false,
  documents: [],
  isLoadingDocument: false,
  sharingPermissions: null,
  isLoadingSharingPermissions: false,
  isLoadingPersonal: false,
  editMode: false,

  scanRecognitionData: null as Nullable<HealthInsuranceRecognitionData>,
  scanDocument: null as Nullable<MediaType>,
};

export const personalSlice = createSlice({
  name: 'personalSlice',
  initialState,
  reducers: {
    updatePersonalByField<T extends keyof PersonalModel>(
      state: { data: Nullable<PersonalModel> },
      action: PayloadAction<{ field: T; value: PersonalModel[T] }>,
    ) {
      const { field, value } = action.payload;
      if (state.data) {
        state.data = {
          ...state.data,
          [field]: value,
        };
      }
    },
    setHealthInsuranceRecognitionData(
      state,
      action: PayloadAction<{
        recognitionData: Nullable<HealthInsuranceRecognitionData>;
        scanDocument: Nullable<MediaType>;
      }>,
    ) {
      state.scanRecognitionData = action.payload.recognitionData;
      state.scanDocument = action.payload.scanDocument;
    },
    resetScanData(state) {
      state.scanRecognitionData = null;
      state.scanDocument = null;
    },
    setHealthInsuranceAiScanDocument(state, action: PayloadAction<{ scanDocument: Nullable<MediaType> }>) {
      state.scanDocument = action.payload.scanDocument;
    },

    setProfileAvatar: (state, action: PayloadAction<AvatarModel>) => {
      if (state.data) {
        state.data = { ...state.data, avatar: action.payload };
      }
    },
    deleteProfileAvatar: (state) => {
      if (state.data) {
        state.data = { ...state.data, avatar: null };
      }
    },
    setProfileBackground: (state, action: PayloadAction<AvatarModel>) => {
      if (state.data) {
        state.data = { ...state.data, background: action.payload };
      }
    },
    deleteProfileBackground: (state) => {
      if (state.data) {
        state.data = { ...state.data, background: null };
      }
    },
    updateGallery: (state, { payload }: PayloadAction<MediaType[]>) => {
      if (state.data) {
        state.data = { ...state.data, gallery: payload };
      }
    },
    updateGeneralInfoDocuments: (state, { payload }: PayloadAction<MediaType[]>) => {
      if (state.data) {
        state.data = { ...state.data, documents: payload };
      }
    },
    updateProfileData: (state, { payload }: PayloadAction<any>) => {
      state.data = { ...state.data, ...payload };
    },
    setEditMode(state, action: PayloadAction<{ editMode: boolean }>) {
      state.editMode = action.payload.editMode;
    },
    setLoadingPersonal(state, action: PayloadAction<{ isLoading: boolean }>) {
      state.isLoadingPersonal = action.payload.isLoading;
    },
    resetPersonalDataStore: () => {
      return initialState;
    },
    // ------------------ health insurance ------------------
    addCreatedHealthInsuranceToList(state, action: PayloadAction<HealthInsurances>) {
      state.healthInsurances = state.healthInsurances
        ? [...state.healthInsurances, action.payload]
        : [action.payload];
    },
    updateHealthInsuranceInList(state, action: PayloadAction<HealthInsurances>) {
      const updatedList =
        state.healthInsurances?.map((item) => {
          if (item.id === action.payload.id) {
            return action.payload;
          }

          return item;
        }) || null;

      state.healthInsurances = updatedList;
    },
    deleteHealthInsuranceFromList(state, action: PayloadAction<{ id: number }>) {
      state.healthInsurances =
        state.healthInsurances?.filter((item) => item.id !== action.payload.id) || null;
    },
    resetHealthInsurance: (state) => {
      state.healthInsurances = null;
      state.isLoadingHealthInsurance = false;
    },
  },
  extraReducers: ({ addCase }) => {
    addCase(getProfileNewInfo.fulfilled, (state, { payload }) => {
      state.data = { ...state.data, ...payload };
    });
    addCase(updateProfileNewGeneralInfo.fulfilled, (state, { payload }) => {
      state.data = { ...state.data, ...payload };
    });
    addCase(updateProfileAppearance.fulfilled, (state, { payload }) => {
      if (state.data) {
        state.data = { ...state.data, appearance: payload };
      }
    });
    addCase(getProfileNewDocuments.fulfilled, (state, { payload }) => {
      state.documents = payload;
      state.isLoadingDocument = true;
    });
    addCase(getBodyArts.fulfilled, (state, { payload }) => {
      const newFilteredData = payload.data.filter(
        (item) => !state.bodyArts?.some((bodyArt) => bodyArt.id === item.id),
      );

      state.bodyArts = state.bodyArts ? [...state.bodyArts, ...newFilteredData] : payload.data;
      state.isLoadingBodyArts = true;

      state.bodyArtsMeta = {
        page: payload.meta.current_page + 1,
        isStopPagination: payload.meta.last_page <= state.bodyArtsMeta.page,
      };
    });
    addCase(createBodyArt.fulfilled, (state, { payload }) => {
      if (state.bodyArts) {
        state.bodyArts.unshift(payload);
      }
    });
    addCase(updateBodyArt.fulfilled, (state, { payload }) => {
      state.bodyArts = state.bodyArts?.length
        ? state.bodyArts.map((item: BodyArtModel) => {
            if (item.id === payload.id) {
              return payload;
            }
            return item;
          })
        : null;
    });
    addCase(deleteBodyArt.fulfilled, (state, { payload }) => {
      state.bodyArts = state.bodyArts?.length
        ? state.bodyArts.filter((item: any) => item.id !== payload)
        : null;
    });
    addCase(getProfileSharingUsers.fulfilled, (state, { payload }) => {
      state.sharingPermissions = payload;
      state.isLoadingSharingPermissions = true;
    });
    addCase(attachUserToShareProfile.fulfilled, (state, { payload }) => {
      state.sharingPermissions?.push(payload);
    });
    addCase(detachUserFromShareProfile.fulfilled, (state, { payload }) => {
      state.sharingPermissions = state.sharingPermissions?.length
        ? state.sharingPermissions.filter((item: any) => item.id !== payload)
        : null;
    });
    addCase(updateUserShareProfilePermissions.fulfilled, (state, { payload }) => {
      state.sharingPermissions = state.sharingPermissions?.length
        ? state.sharingPermissions.map((item: any) => {
            if (item.id === payload.id) {
              return payload;
            }
            return item;
          })
        : null;
    });

    // ---------------- Health Insurance ----------------
    addCase(getHealthInsurances.fulfilled, (state, { payload }) => {
      state.healthInsurances = payload;
      state.isLoadingHealthInsurance = true;
    });
  },
});

export const {
  setProfileAvatar,
  deleteProfileAvatar,
  setProfileBackground,
  deleteProfileBackground,
  updateGallery,
  updateGeneralInfoDocuments,
  updateProfileData,
  resetPersonalDataStore,
  addCreatedHealthInsuranceToList,
  updateHealthInsuranceInList,
  deleteHealthInsuranceFromList,
  resetHealthInsurance,
  setEditMode,
  setLoadingPersonal,
  updatePersonalByField,
  // ai
  setHealthInsuranceAiScanDocument,
  setHealthInsuranceRecognitionData,
  resetScanData,
} = personalSlice.actions;
export default personalSlice.reducer;
